class FinancePageOptions extends HTMLElement {
  connectedCallback () {
    const configScript = this.querySelector('.finance-page-options__config')
    if (!configScript) return

    try {
      this.config = JSON.parse(configScript.textContent)
    } catch (e) {
      this.config = null
      return
    }

    this.tabsEl = this.querySelector('.finance-page-options__tabs')
    this.panelsEl = this.querySelector('.finance-page-options__panels')
    this.nativePanelsEl = this.querySelector('.finance-page-options__native-panels')
    this.productRowEl = this.querySelector('.finance-page-options__product-row')
    this.productCarouselEl = this.querySelector('.finance-page-options__product-carousel')

    if (!this.tabsEl || !this.panelsEl) return

    this.config.providers = this.config.providers || []
    this.config.nativeProviders = this.config.nativeProviders || []
    if (this.config.providers.length === 0 && this.config.nativeProviders.length === 0) return

    this.activeProviderIndex = 0
    this.activeProductIndex = 0
    this.activePriceCents = this.getDefaultPriceCents()

    this.setupProductTabs()
    this.renderTabs()
    this.renderPanels()
    this.updateProductRowVisibility()
  }

  getDefaultPriceCents () {
    const products = this.config.products || []
    if (products.length > 0 && typeof products[0].price === 'number') {
      return products[0].price
    }
    return this.config.fallbackPriceCents || 0
  }

  setupProductTabs () {
    const tabs = this.querySelectorAll('.finance-page-options__product-tab[data-product-index]')
    if (tabs.length === 0) return

    tabs.forEach(tab => {
      if (tab.classList.contains('finance-page-options__product-tab--static')) return
      tab.addEventListener('click', () => {
        this.switchProduct(parseInt(tab.dataset.productIndex, 10))
      })
    })
  }

  setActiveProduct (index) {
    const products = this.config.products || []
    if (!Number.isNaN(index) && products[index]) {
      this.activeProductIndex = index
      this.activePriceCents = products[index].price
    } else {
      this.activePriceCents = this.config.fallbackPriceCents || 0
    }

    this.updateProductTabStates()
    this.renderPanels()
  }

  switchProduct (index) {
    if (index === this.activeProductIndex) return
    this.setActiveProduct(index)
    this.scrollProductCarouselTo(index)
  }

  scrollProductCarouselTo (index) {
    if (!this.productRowEl) return

    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches) {
      if (!this.productCarouselEl) return
      this.productCarouselEl.dispatchEvent(new CustomEvent('carousel:scrollTo', {
        detail: { index, jump: false },
        bubbles: true,
      }))
      return
    }

    const tab = this.productRowEl.querySelector(`.finance-page-options__product-tab[data-product-index="${index}"]`)
    const slide = tab?.closest('.finance-page-options__product-carousel-slide')
    const viewport = this.productRowEl.querySelector('.finance-page-options__product-carousel-viewport')
    if (!slide || !viewport) return

    const viewportRect = viewport.getBoundingClientRect()
    const slideRect = slide.getBoundingClientRect()
    const scrollPadding = 16
    const targetLeft = viewport.scrollLeft + (slideRect.left - viewportRect.left) - scrollPadding
    viewport.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' })
  }

  updateProductTabStates () {
    if (!this.productRowEl) return

    this.productRowEl.querySelectorAll('.finance-page-options__product-tab[data-product-index]').forEach(tab => {
      const tabIndex = parseInt(tab.dataset.productIndex, 10)
      const isActive = tabIndex === this.activeProductIndex
      tab.setAttribute('aria-selected', isActive)
      const tick = tab.querySelector('.finance-page-options__product-tab-tick')
      if (isActive) {
        tab.classList.remove('border-clay')
        tab.classList.add('border-black')
        if (tick) tick.classList.remove('hidden')
      } else {
        tab.classList.remove('border-black')
        tab.classList.add('border-clay')
        if (tick) tick.classList.add('hidden')
      }
    })
  }

  getAllProviders () {
    return [...this.config.providers, ...this.config.nativeProviders]
  }

  renderTabs () {
    const providers = this.getAllProviders()
    const hasMultiple = providers.length > 1
    let html = ''

    if (!hasMultiple) {
      const logoHtml = this.renderProviderLogo(providers[0])
      if (logoHtml) {
        html = `<div class="finance-page-options__single-logo flex items-center justify-center py-2">${logoHtml}</div>`
      }
      this.tabsEl.innerHTML = html
      return
    }

    providers.forEach((provider, i) => {
      const isActive = i === this.activeProviderIndex
      const borderClass = isActive ? 'border-black' : 'border-clay'
      const logoHtml = this.renderProviderLogo(provider)
      const tagHtml = provider.tag
        ? `<span class="finance-page-options__tab-tag mt-1.5 text-center leading-relaxed"><span class="inline box-decoration-clone text-xs font-semibold rounded-md px-2.5 py-0.5" style="${this.getTagStyle(provider.tagColor)}">${this.escapeHtml(provider.tag)}</span></span>`
        : ''
      const tickSvg = this.config.tickIconSvg || ''
      const tickHtml = `<span class="finance-page-options__tab-tick absolute top-1.5 right-1.5 flex items-center justify-center w-6 h-6 rounded-full bg-success text-white p-1 ${isActive ? '' : 'hidden'}">${tickSvg}</span>`
      html += `
        <button
          type="button"
          class="finance-page-options__tab relative ${borderClass} border rounded-lg w-42.5 min-h-21 shrink-0 snap-start md:w-auto md:flex-1 md:max-w-42.5 md:min-w-0 md:shrink flex flex-col items-center justify-start gap-1.5 pt-3 pb-2.5 px-3 cursor-pointer bg-white transition-colors"
          role="tab"
          aria-selected="${isActive}"
          data-provider-index="${i}"
        >${tickHtml}${logoHtml}${tagHtml}</button>`
    })

    this.tabsEl.innerHTML = html

    this.tabsEl.querySelectorAll('.finance-page-options__tab').forEach(tab => {
      tab.addEventListener('click', () => {
        this.switchProvider(parseInt(tab.dataset.providerIndex, 10))
      })
    })
  }

  switchProvider (index) {
    if (index === this.activeProviderIndex) return
    this.activeProviderIndex = index

    let activeTab = null
    this.tabsEl.querySelectorAll('.finance-page-options__tab').forEach(tab => {
      const tabIndex = parseInt(tab.dataset.providerIndex, 10)
      const isActive = tabIndex === index
      tab.setAttribute('aria-selected', isActive)
      const tick = tab.querySelector('.finance-page-options__tab-tick')
      if (isActive) {
        tab.classList.remove('border-clay')
        tab.classList.add('border-black')
        if (tick) tick.classList.remove('hidden')
        activeTab = tab
      } else {
        tab.classList.remove('border-black')
        tab.classList.add('border-clay')
        if (tick) tick.classList.add('hidden')
      }
    })

    this.scrollCarouselItemIntoView(this.tabsEl, activeTab)
    this.renderPanels()
    this.updateProductRowVisibility()
  }

  updateProductRowVisibility () {
    if (!this.productRowEl) return
    const activeProvider = this.getAllProviders()[this.activeProviderIndex]
    const isNativeApp = activeProvider?.kind === 'app'
    const isCustomProvider = !activeProvider || !!activeProvider.termsContent || isNativeApp
    this.productRowEl.classList.toggle('finance-page-options__product-row--disabled', isCustomProvider)
  }

  scrollCarouselItemIntoView (container, tab) {
    if (!tab || !container) return
    if (typeof window === 'undefined' || !window.matchMedia) return
    if (window.matchMedia('(min-width: 768px)').matches) return

    const containerRect = container.getBoundingClientRect()
    const tabRect = tab.getBoundingClientRect()
    const scrollPadding = 16
    const targetLeft = container.scrollLeft + (tabRect.left - containerRect.left) - scrollPadding
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' })
  }

  renderPanels () {
    const providers = this.config.providers
    const isNative = this.activeProviderIndex >= providers.length

    if (isNative) {
      this.panelsEl.classList.add('hidden')
      if (this.nativePanelsEl) {
        this.nativePanelsEl.classList.remove('hidden')
        const nativeIndex = this.activeProviderIndex - providers.length
        this.nativePanelsEl.querySelectorAll('.finance-page-options__native-panel').forEach(panel => {
          panel.classList.toggle('hidden', parseInt(panel.dataset.nativePanelIndex, 10) !== nativeIndex)
        })
        const activeNativePanel = this.nativePanelsEl.querySelector(`.finance-page-options__native-panel[data-native-panel-index="${nativeIndex}"]`)
        const activeNativeProvider = this.config.nativeProviders[nativeIndex]
        if (activeNativeProvider?.kind === 'paypal' && activeNativePanel) {
          this.renderPayPalMessage(activeNativePanel)
        }
      }
      return
    }

    this.panelsEl.classList.remove('hidden')
    if (this.nativePanelsEl) this.nativePanelsEl.classList.add('hidden')

    const activeProvider = providers[this.activeProviderIndex]
    if (!activeProvider) return

    const pricePounds = (this.activePriceCents || 0) / 100
    const terms = activeProvider.terms || []
    const termsContent = activeProvider.termsContent || ''
    const howToApply = activeProvider.howToApply || ''
    const t = this.config?.translations || {}

    let html = ''
    if (activeProvider.tabText) {
      html += `<div class="finance-page-options__tab-text text-base text-black whitespace-pre-line">${this.escapeHtml(activeProvider.tabText)}</div>`
    }
    if (termsContent) {
      html += `<div class="finance-page-options__terms-content tb-page-content text-base text-black leading-snug">${this.decodeHtml(termsContent)}</div>`
    } else if (terms.length === 0) {
      html += `<p class="text-sm text-iron text-center">${this.escapeHtml(t.noOptions || '')}</p>`
    } else {
      html += '<div class="finance-page-options__cards flex flex-col gap-3.5">'
      terms.forEach(term => {
        html += this.generateFinanceCard(term, pricePounds)
      })
      html += '</div>'
    }

    if (howToApply) {
      const howToApplyTitle = t.howToApplyTitle || 'How to apply:'
      html += `
        <div class="finance-page-options__how-to-apply flex flex-col gap-2.5 mt-4 pb-5 border-b border-clay">
          <div class="finance-page-options__how-to-apply-title font-medium text-lg leading-snug text-black tb-in-page-content [&>p]:mb-0">${this.decodeHtml(howToApplyTitle)}</div>
          <div class="tb-in-page-content tb-list-fix text-base text-black">${this.decodeHtml(howToApply)}</div>
        </div>`
    }

    if (activeProvider.representativeExample) {
      if (howToApply) {
        html += '<div class="finance-page-options__divider bg-clay/50 h-px w-full"></div>'
      }
      html += `
        <div class="finance-page-options__repr flex flex-col gap-1">
          <div class="finance-page-options__repr-body tb-page-content text-sm text-black leading-snug">${this.decodeHtml(activeProvider.representativeExample)}</div>
        </div>`
    }

    this.panelsEl.innerHTML = html
  }

  renderPayPalMessage (panelEl) {
    // Deliberately not using PayPal's `data-pp-message` attribute here: that
    // triggers the SDK's own auto-scan/auto-render on script load, which
    // races against this explicit render() call (needed so the amount can
    // track the selected model) and throws "container element removed from
    // DOM" when both try to render into the same element.
    const el = panelEl.querySelector('.finance-page-options__pp-message')
    if (!el) return

    // Opt-in: PayPal's own guidance is to leave the message generic (no
    // amount) unless the merchant explicitly asks for it to track the
    // selected model's price, via this attribute on the pasted embed.
    if (el.hasAttribute('data-pp-dynamic-amount')) {
      el.setAttribute('data-pp-amount', ((this.activePriceCents || 0) / 100).toFixed(2))
    }

    const paypalNs = window.paypal || window.PayPalSDK
    if (!paypalNs?.Messages) {
      this._paypalRetryCount = (this._paypalRetryCount || 0) + 1
      if (this._paypalRetryCount > 100) return
      setTimeout(() => this.renderPayPalMessage(panelEl), 50)
      return
    }
    this._paypalRetryCount = 0

    const parsedAmount = parseFloat(el.dataset.ppAmount)
    const messageOptions = {
      style: {
        layout: el.dataset.ppStyleLayout,
        ratio: el.dataset.ppStyleRatio,
        color: el.dataset.ppStyleColor,
        logo: { type: el.dataset.ppStyleLogoType },
      },
    }
    if (!Number.isNaN(parsedAmount)) messageOptions.amount = parsedAmount

    el.innerHTML = ''
    paypalNs.Messages(messageOptions).render(el)
  }

  generateFinanceCard (term, price) {
    const FU = window.FinanceUtils
    const rateType = term.rateType || 'apr'
    const monthlyPayment = Math.ceil(FU.calculateMonthlyPayment(price, term.rate, term.period, rateType))
    const totalPayable = Math.ceil(FU.calculateTotalPayable(price, term.rate, term.period, rateType))

    const formattedMonthly = this.formatPrice(monthlyPayment)
    const formattedTotal = this.formatPrice(totalPayable)
    const t = this.config?.translations || {}
    const rateNum = Number(term.rate)
    const isZero = rateNum === 0 && !Number.isNaN(rateNum)
    const rateTemplate = rateType === 'fee'
      ? (t.fee || '{{ rate }}% fee')
      : (t.interest || '{{ rate }}% APR')
    const interestLabel = isZero
      ? (t.interestZero || '0% APR')
      : rateTemplate.replace('{{ rate }}', term.rate)
    const totalLabelRaw = t.totalPayable || 'Total payable:'
    const perMonthSuffix = t.perMonthSuffix || '/month'

    const totalHtml = totalLabelRaw.includes('{{ amount }}')
      ? `<span>${this.escapeHtml(totalLabelRaw).replace('{{ amount }}', formattedTotal)}</span>`
      : `<span>${this.escapeHtml(totalLabelRaw)}</span><span>${formattedTotal}</span>`

    return `
      <div class="finance-card bg-oat/40 border border-clay rounded-lg px-3.5 py-3 flex flex-col">
        <div class="finance-card__header flex items-center justify-between gap-3 pb-2.5 mb-2.5 border-b border-clay/60">
          <span class="finance-card__title font-semibold text-lg leading-snug text-black">${term.period} × ${formattedMonthly}${this.escapeHtml(perMonthSuffix)}</span>
          <span class="finance-card__badge whitespace-nowrap border border-orange text-orange font-semibold text-[15px] leading-none rounded-[3px] px-1.5 py-1">${this.escapeHtml(interestLabel)}</span>
        </div>
        <div class="finance-card__total flex flex-wrap gap-x-1.5 text-sm font-medium text-black">
          ${totalHtml}
        </div>
      </div>`
  }

  renderProviderLogo (provider) {
    if (provider.logoSvg) {
      const decoded = this.decodeHtml(provider.logoSvg)
      return `<span class="finance-page-options__logo-svg inline-flex items-center justify-center shrink-0" style="height: 32px; max-width: 100%;"><span class="h-full flex items-center justify-center [&>svg]:h-full [&>svg]:w-auto">${decoded}</span></span>`
    } else if (provider.logo) {
      const imgStyle = provider.tag
        ? 'height: 28px; width: auto; max-width: 100%;'
        : 'height: 32px; width: auto; max-width: 100%; display: block; margin: 0 auto;'
      return `<img class="finance-page-options__logo-img" src="${this.escapeHtml(provider.logo)}" alt="" style="${imgStyle}" />`
    }
    return ''
  }

  getTagStyle (color) {
    const map = {
      green: 'background: #dcfce7; color: #166534;',
      orange: 'background: #fff1e6; color: #c2410c;',
      blue: 'background: #2563EB1A; color: #2563EB;',
      neutral: 'background: #f3f4f6; color: #4b5563;',
    }
    return map[color] || map.neutral
  }

  formatPrice (amount) {
    if (window.FinanceUtils?.formatMoneyAmount) {
      return window.FinanceUtils.formatMoneyAmount(amount)
    }
    const currencySymbol = this.getCurrencySymbol()
    return `${currencySymbol}${Math.round(amount)}`
  }

  getCurrencySymbol () {
    const currencyCode = window.Shopify?.currency?.active
    switch (currencyCode) {
      case 'USD':
        return '$'
      case 'EUR':
        return '€'
      case 'GBP':
        return '£'
      default:
        return ''
    }
  }

  escapeHtml (str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.appendChild(document.createTextNode(str))
    return div.innerHTML
  }

  decodeHtml (html) {
    if (!html) return ''
    const txt = document.createElement('textarea')
    txt.innerHTML = html
    return txt.value
  }
}

if (!customElements.get('finance-page-options')) {
  customElements.define('finance-page-options', FinancePageOptions)
}
